import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ToolResultCard.tsx -- inline rendering of agent-generated documents
// in the chat bubble. Replaces the agent's hallucinated
// "![file](sandbox:/...)" markdown links with a real, clickable card.
//
// Two render branches:
//   - image content-type -> inline thumbnail using the canonical
//     /api/p/<projectId>/documents/<docId>/content/private endpoint
//     (same URL DocumentPreview uses; browsers send our auth cookie
//     automatically on <img> requests).
//   - everything else    -> file card with content-type icon +
//     filename + size.
//
// Both branches:
//   - left-click  -> open preview (navigates to /p/<pid>/d/<docId>
//                    which the desktop-shell's URL effect resolves
//                    to the DocumentPreview modal)
//   - right-click -> context menu with Preview / Download / Open in
//                    new tab / Show in aidocs.
//
// The card is the agent's answer to the user's "I should be able to
// click on the document right there" UX request. The card itself
// mounts inside the chat bubble; it doesn't own any modal state.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDesktop } from '../../hooks/useDesktop';
export function ToolResultCard({ doc, projectId, aidocsFolderId }) {
    const navigate = useNavigate();
    const { select } = useDesktop();
    // Right-click menu position. null = closed.
    const [menu, setMenu] = useState(null);
    const menuRef = useRef(null);
    // Outside-click + Escape close the menu. Mounted only when menu
    // is open. Mirrors the existing ContextMenu component's pattern;
    // we don't reuse that component because it's opinionated to
    // desktop entries (CatalogEntry + per-callback props), not a
    // generic items-array consumer. If we ever add more chat-card
    // menus this is the place to factor a shared <FloatingMenu>.
    useEffect(() => {
        if (!menu)
            return;
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenu(null);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setMenu(null);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [menu]);
    const isImage = doc.contentType.startsWith('image/');
    const contentUrl = `/api/p/${projectId}/documents/${doc.documentId}/content/private`;
    const downloadUrl = `${contentUrl}?download=1`;
    const onPreview = useCallback(() => {
        // Same path as desktop-icon double-click: drive via URL so the
        // shell's /d/:documentId effect handles the modal mount. Keeps
        // chat-card and desktop-icon flows converged on one preview
        // entrypoint.
        navigate(`/p/${projectId}/d/${doc.documentId}`);
    }, [navigate, projectId, doc.documentId]);
    const onDownload = useCallback(() => {
        // window.location.href triggers the browser's download flow with
        // the BFF's attachment disposition. Same shape as
        // DocumentPreview's download button.
        window.location.href = downloadUrl;
    }, [downloadUrl]);
    const onOpenNewTab = useCallback(() => {
        // Open the bytes in a new tab without navigating away. Useful
        // for PDFs the user wants to keep open while continuing to
        // chat. Browsers honour content-type from the BFF's S3 redirect
        // so PDFs render in the built-in viewer, images render inline,
        // text renders as text.
        window.open(contentUrl, '_blank', 'noopener,noreferrer');
    }, [contentUrl]);
    const onNavigateToAidocs = useCallback(() => {
        if (!aidocsFolderId)
            return;
        // Same side-effect chain that fdn_document_generate's onSuccess
        // emits. Selects the doc once we land in aidocs so it's
        // visually highlighted.
        navigate(`/p/${projectId}/f/${aidocsFolderId}`);
        // Best-effort selection; if the catalog hasn't loaded yet the
        // select call is a no-op and the user can find it via the
        // recently-modified ordering. The navigate alone is sufficient
        // for the user to "see" the doc in context.
        select({
            itemId: doc.documentId,
            type: 'document',
            displayName: doc.filename,
        }, false, false);
    }, [navigate, projectId, aidocsFolderId, doc.documentId, doc.filename, select]);
    const onContextMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    }, []);
    // Inline menu items. Each entry: { label, onClick, disabled?,
    // separatorBefore? }. Trivial inline shape -- not worth a typed
    // export until we have multiple chat-card menus.
    const menuItems = [
        { label: 'Preview', onClick: onPreview },
        { label: 'Download', onClick: onDownload },
        { label: 'Open in new tab', onClick: onOpenNewTab },
        {
            label: 'Show in aidocs',
            onClick: onNavigateToAidocs,
            disabled: !aidocsFolderId,
            separatorBefore: true,
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `tool-result-card ${isImage ? 'tool-result-card-image' : 'tool-result-card-file'}`, onClick: onPreview, onContextMenu: onContextMenu, role: "button", tabIndex: 0, title: doc.filename, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPreview();
                    }
                }, children: isImage ? (_jsxs(_Fragment, { children: [_jsx("img", { className: "tool-result-card-thumb", src: contentUrl, alt: doc.filename, loading: "lazy", draggable: false }), _jsxs("div", { className: "tool-result-card-meta", children: [_jsx("span", { className: "tool-result-card-name", children: doc.filename }), _jsx("span", { className: "tool-result-card-size", children: formatSize(doc.fileSize) })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "tool-result-card-icon", "aria-hidden": true, children: fileGlyph(doc.contentType) }), _jsxs("div", { className: "tool-result-card-stack", children: [_jsx("div", { className: "tool-result-card-name", children: doc.filename }), _jsxs("div", { className: "tool-result-card-meta-row", children: [_jsx("span", { className: "tool-result-card-type", children: shortContentType(doc.contentType) }), doc.fileSize > 0 && (_jsx("span", { className: "tool-result-card-size", children: formatSize(doc.fileSize) }))] })] })] })) }), menu && (_jsx("div", { ref: menuRef, className: "tool-result-card-menu", style: { left: menu.x, top: menu.y }, role: "menu", children: menuItems.map((it, i) => (_jsxs(React.Fragment, { children: [it.separatorBefore && (_jsx("div", { className: "tool-result-card-menu-sep", role: "separator" })), _jsx("button", { type: "button", className: "tool-result-card-menu-item", onClick: () => {
                                setMenu(null);
                                it.onClick();
                            }, disabled: it.disabled, role: "menuitem", children: it.label })] }, i))) }))] }));
}
// --- small helpers ---
function formatSize(n) {
    if (!n || n < 0)
        return '';
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024)
        return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
function fileGlyph(contentType) {
    // Single-character glyphs for the file-card branch. Mirrors the
    // desktop-icon mapping (DesktopIcon.tsx::fileGlyph) so the same
    // file looks the same in chat and on the canvas.
    if (contentType.startsWith('text/markdown'))
        return '\uD83D\uDCDD'; // memo
    if (contentType.startsWith('text/html'))
        return '\uD83C\uDF10'; // globe
    if (contentType.startsWith('text/'))
        return '\uD83D\uDCC4'; // page
    if (contentType.includes('pdf'))
        return '\uD83D\uDCD5'; // book
    if (contentType.includes('spreadsheet') || contentType.includes('csv'))
        return '\uD83D\uDCCA'; // chart
    if (contentType.includes('presentation'))
        return '\uD83D\uDCC8'; // upward chart
    if (contentType.includes('wordprocessing') || contentType.includes('document'))
        return '\uD83D\uDCC4';
    if (contentType.includes('python') || contentType.includes('javascript') || contentType.includes('go'))
        return '\u003C\u002F\u003E';
    if (contentType.startsWith('audio/'))
        return '\uD83C\uDFB5'; // note
    if (contentType.startsWith('video/'))
        return '\uD83C\uDFAC'; // clapper
    return '\uD83D\uDCC4'; // generic page
}
function shortContentType(ct) {
    // Trim noisy MIME types for the tiny meta line under the filename.
    // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
    // becomes "spreadsheet"; text/markdown becomes "markdown".
    if (ct.startsWith('text/'))
        return ct.slice(5);
    if (ct.startsWith('image/'))
        return ct.slice(6);
    if (ct.startsWith('audio/'))
        return ct.slice(6);
    if (ct.startsWith('video/'))
        return ct.slice(6);
    if (ct.includes('spreadsheet'))
        return 'spreadsheet';
    if (ct.includes('presentation'))
        return 'presentation';
    if (ct.includes('wordprocessing'))
        return 'document';
    if (ct.includes('pdf'))
        return 'pdf';
    return ct;
}
